import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Checkbox,
    SelectChangeEvent,
    SelectProps,
} from '@mui/material'
import { sortBy } from 'lodash-es'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import { FilledFormControl } from '../FilledFormControl/FilledFormControl'
import React, { ReactNode, useCallback, useMemo } from 'react'
import { TOption } from '../OWSelect'

export interface ISelectorProps extends SelectProps {
    options: TOption[]
    hasEmpty?: boolean
    onChangeCallback?: (value: any | any[]) => void
    disabled?: boolean
    sortLabel?: boolean
    error?: boolean
    helperText?: string
    loading?: boolean
    //multiple
    hasAllOption?: boolean
    allOptionText?: string
    nonOptionText?: string
}
export function Selector(props: ISelectorProps) {
    const {
        label,
        options,
        hasEmpty,
        onChangeCallback,
        sortLabel = false,
        error,
        helperText,
        value,
        id,
        disabled,
        onChange,
        loading,
        multiple,
        variant: inputVariant,
        hasAllOption,
        allOptionText = 'All',
        nonOptionText = 'None',
        sx,
        ...otherFields
    } = props
    const pickedTextFieldObj = (({ fullWidth }) => ({
        fullWidth,
        sx,
    }))(props)
    const nonOption = useMemo(() => ({ label: nonOptionText, value: 'none' }), [
        nonOptionText,
    ])
    const allOption = useMemo(() => ({ label: allOptionText, value: 'all' }), [
        allOptionText,
    ])
    const selectOptions = sortLabel ? sortBy(options, 'label') : options
    const { isMobile } = useScreenSize()

    const Component = isMobile && !label ? FilledFormControl : FormControl
    const variant = inputVariant
        ? inputVariant
        : isMobile && label
        ? 'filled'
        : 'outlined'

    const handleChange = useCallback(
        (e: SelectChangeEvent<unknown>, c: ReactNode) => {
            if (multiple) {
                let values = e.target.value as string[]
                if (values.includes(allOption.value))
                    values = options.map((a) => a.value)
                if (values.includes(nonOption.value)) values = []
                onChange?.(
                    {
                        ...e,
                        target: { ...e.target, value: values },
                    } as SelectChangeEvent<unknown>,
                    values,
                )
                onChangeCallback?.(values)
            } else {
                onChange?.(e, c)
                onChangeCallback?.(e.target.value)
            }
        },
        [
            allOption.value,
            multiple,
            nonOption.value,
            onChange,
            onChangeCallback,
            options,
        ],
    )

    return (
        <Component
            error={error && !!helperText}
            variant={variant}
            {...pickedTextFieldObj}
        >
            <InputLabel size="small" id={id && `${id}-label`}>
                {loading ? 'loading' : label}
            </InputLabel>
            <Select
                label={label}
                labelId={id && `${id}-label`}
                id={id && `${id}`}
                size="small"
                value={value ?? ''}
                multiple={multiple}
                onChange={handleChange}
                disabled={disabled}
                renderValue={
                    multiple
                        ? (values) =>
                              (value as string[])
                                  .map(
                                      (v) =>
                                          options.find((c) => c.value === v)
                                              ?.label,
                                  )
                                  .join(', ')
                        : undefined
                }
                {...otherFields}
            >
                {hasEmpty && !multiple && (
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                )}
                {hasAllOption &&
                    multiple &&
                    ((value as string[]).length === options.length ? (
                        <MenuItem value={nonOption.value}>
                            <em>{nonOption.label}</em>
                        </MenuItem>
                    ) : (
                        <MenuItem value={allOption.value}>
                            <em>{allOption.label}</em>
                        </MenuItem>
                    ))}
                {selectOptions.map((option) => {
                    return multiple ? (
                        <MenuItem
                            key={option.key ?? option.value}
                            value={option.value}
                        >
                            <Checkbox
                                checked={
                                    (value as string[]).indexOf(option.value) >
                                    -1
                                }
                            />
                            {option.label}
                        </MenuItem>
                    ) : (
                        <MenuItem
                            key={option.key ?? option.value}
                            value={option.value}
                        >
                            {option.label}
                        </MenuItem>
                    )
                })}
            </Select>
            {error && !!helperText && (
                <FormHelperText>{helperText}</FormHelperText>
            )}
        </Component>
    )
}
